import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Tab,
	TextField,
	Typography
} from "@mui/material";
import { ColDef } from "ag-grid-enterprise";
import { MuiColorInput } from "mui-color-input";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddButton, CancelButton, DeleteButton, RemoveButton } from "~/components/Buttons";
import SavedRules from "~/components/grid/conditionalFormatting/SavedRules";
import verifyFormattingRule from "~/components/grid/conditionalFormatting/verifyFormattingRule";
import ModalDialog from "~/components/ModalDialog";
import SpacedGridContainer from "~/components/SpacedGridContainer";
import SpacedGridItem from "~/components/SpacedGridItem";
import NumberFieldBase from "~/components/textfield/NumberFieldBase";
import { GetUserAppSettingsQueryVariables, useGetUserAppSettingsQuery } from "~/gql/queries/userAppSetting.generated";
import { camelToTitle } from "~/helpers/camelToTitle";
import logger from "~/helpers/logger";
import snackMessages from "~/helpers/snackMessages";
import wsId from "~/helpers/wsId";
import useUserAppSettingMutations from "~/hooks/mutations/useUserAppSettingMutations";
import { useAppSelector } from "~/hooks/reduxHooks";
import { useSnacks } from "~/providers/SnackProvider";

interface FormattingPanelProps {
	columns: ColDef[];
	formattingPanelOpen: boolean;
	gridDataKey: string;
	rules: GridFormattingRule[];
	setFormattingPanelOpen: (open: boolean) => void;
	setRules: (rules: GridFormattingRule[]) => void;
}

const ENTITY_LABEL = "Rules";

const defaultCondition: ConditionDef = {
	column: "",
	dataType: "numeric",
	type: { average: "above", numeric: "greaterThan", ranking: "top", string: "contains", unique: "unique" },
	value: "",
	value2: ""
};

const getNewRule = (): GridFormattingRule => {
	return {
		active: true,
		andor: "and",
		conditions: [defaultCondition],
		defaultActive: false,
		id: "",
		ruleType: "value",
		style: { backgroundColor: "#000000ff", color: "#ffffffff" },
		style2: { backgroundColor: "#000000ff", color: "#ffffffff" },
		styleType: "single"
	};
};

const FormattingPanel: React.FC<FormattingPanelProps> = ({
	columns,
	formattingPanelOpen,
	gridDataKey,
	rules,
	setFormattingPanelOpen,
	setRules
}) => {
	const [newRule, setNewRule] = useState<GridFormattingRule>(getNewRule());
	const [addEditRule, setAddEditRule] = useState<boolean>(false);
	const [activeRules, setActiveRules] = useState<{ id: string; active: boolean }[]>([]);
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();
	const { updateUserAppSetting } = useUserAppSettingMutations();
	const [tabValue, setTabValue] = useState("1");

	const email = useAppSelector((state) => state.settings.email);

	const getUserAppSettingsQueryVariables: GetUserAppSettingsQueryVariables = {
		first: 50,
		where: {
			emailAddress: { eq: email },
			group: { eq: gridDataKey },
			name: { eq: "gridFormattingRules" }
		}
	};

	const {
		data: savedRules,
		error: savedRulesError,
		isLoading: savedRulesLoading
	} = useGetUserAppSettingsQuery(getUserAppSettingsQueryVariables);
	useEffect(() => {
		if (savedRules?.userAppSettings && savedRules.userAppSettings.nodes?.length) {
			try {
				const encodedRules = savedRules.userAppSettings.nodes?.[0].base64Settings;
				if (!encodedRules) return;
				const decodedRules = atob(encodedRules);
				const parsedRules = JSON.parse(decodedRules) as GridFormattingRule[];
				const updatedActiveRules = parsedRules.map((rule) => {
					const activeRule = activeRules.find((activeRule) => activeRule.id === rule.id);
					return activeRule ? { active: activeRule.active, id: rule.id } : { active: rule.defaultActive, id: rule.id };
				});
				// Set active to activeRules
				parsedRules.forEach((rule) => {
					const activeRule = updatedActiveRules.find((activeRule) => activeRule.id === rule.id);
					if (activeRule) {
						rule.active = activeRule.active;
					}
				});
				setRules(parsedRules);
				setActiveRules(updatedActiveRules);
			} catch (error) {
				logger.error("Error parsing saved rules:", error);
			}
		}
	}, [savedRules?.userAppSettings, activeRules.find, setRules]);

	useEffect(() => {
		if (savedRulesError) {
			logger.error(snackMessages.loadError(ENTITY_LABEL), savedRulesError);
			openSnack(snackMessages.loadError(ENTITY_LABEL), "error");
		}
	}, [savedRulesError, openSnack]);

	if (savedRulesLoading) {
		return (
			<ModalDialog
				close={() => setFormattingPanelOpen(false)}
				open={formattingPanelOpen}
				title={t("grid.conditionalFormatting.title")}
			>
				<Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", padding: 4 }}>
					<CircularProgress />
				</Box>
			</ModalDialog>
		);
	}

	const addRule = () => {
		const verifyErrors = verifyFormattingRule(newRule);
		if (verifyErrors.length) {
			openSnack(verifyErrors.join(", "), "error");
			return;
		}
		const existingRuleIndex = rules.findIndex((rule) => rule.id === newRule.id);
		const updatedRules = [...rules];
		if (existingRuleIndex !== -1) {
			updatedRules[existingRuleIndex] = { ...newRule };
		} else {
			const ruleId = wsId();
			updatedRules.push({ ...newRule, active: true, id: ruleId });
			// Set the active state of the new rule
			const newActiveRules = [...activeRules];
			newActiveRules.push({ active: true, id: ruleId });
			setActiveRules(newActiveRules);
		}
		saveRules(updatedRules);
		setNewRule(getNewRule());
		setAddEditRule(false);
		setTabValue("1");
	};

	const addCondition = () => {
		const updatedConditions = [...newRule.conditions];
		updatedConditions.push(defaultCondition);
		setNewRule({ ...newRule, conditions: updatedConditions });
	};

	const deleteCondition = (index: number) => {
		setTabValue(String(index));
		const updatedConditions = [...newRule.conditions];
		updatedConditions.splice(index, 1);
		setNewRule({ ...newRule, conditions: updatedConditions });
	};

	const deleteRule = (id: string) => {
		const updatedRules = rules.filter((rule) => rule.id !== id);
		setRules(updatedRules);
		saveRules(updatedRules);
		setNewRule(getNewRule());
		setAddEditRule(false);
	};

	const editRule = (rule: GridFormattingRule) => {
		setNewRule(rule);
		setAddEditRule(true);
	};

	const saveRules = async (updatedRules: GridFormattingRule[]) => {
		const encodedRules = btoa(JSON.stringify(updatedRules));
		const result = await updateUserAppSetting({
			base64Settings: encodedRules,
			emailAddress: email,
			group: gridDataKey,
			name: "gridFormattingRules"
		});
		if (result) {
			openSnack(snackMessages.saveSuccess(ENTITY_LABEL), "success");
		}
	};

	const showFor = (
		ruleTypes: RuleType[],
		currentRuleType: RuleType,
		conditionTypes?: ConditionType[],
		currentConditionType?: ConditionType
	): boolean => {
		let result = ruleTypes.includes(currentRuleType);
		if (result) {
			if (conditionTypes) {
				if (!currentConditionType) {
					result = false;
				} else {
					result = conditionTypes.includes(currentConditionType);
				}
			}
		}
		return result;
	};

	const tabChange = (_event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
	};

	const toggleActive = (id: string) => {
		const updatedRules = rules.map((rule) => {
			if (rule.id === id) {
				return { ...rule, active: !rule.active };
			}
			return rule;
		});
		setRules(updatedRules);
		//reduce updatedRules to active rules
		const activeRules = updatedRules.map((rule) => {
			return { active: rule.active, id: rule.id };
		});
		setActiveRules(activeRules);
	};

	const updateCondition = (index: number, condition: Partial<GridFormattingRule["conditions"][number]>) => {
		const updatedConditions = [...newRule.conditions];
		updatedConditions[index] = { ...updatedConditions[index], ...condition };
		setNewRule({ ...newRule, conditions: updatedConditions });
	};

	if (addEditRule) {
		return (
			<ModalDialog
				close={() => setFormattingPanelOpen(false)}
				open={formattingPanelOpen}
				showCloseButton={false}
				title={t("grid.conditionalFormatting.title")}
			>
				<Typography sx={{ marginLeft: 1 }} variant="h6">
					{newRule.id === "" ? t("grid.conditionalFormatting.addRule") : t("grid.conditionalFormatting.editRule")}
				</Typography>
				<SpacedGridContainer padding={1}>
					<SpacedGridItem maxCols={3}>
						<FormControl fullWidth>
							<InputLabel>{t("grid.conditionalFormatting.ruleType")}</InputLabel>
							<Select
								disabled={!(newRule.id === "")}
								label={t("grid.conditionalFormatting.ruleLogic")}
								onChange={(e) => setNewRule({ ...newRule, ruleType: e.target.value as RuleType })}
								value={newRule.ruleType}
							>
								<MenuItem value="value">{t("grid.conditionalFormatting.ruleTypeOptions.value")}</MenuItem>
								<MenuItem value="ranking">{t("grid.conditionalFormatting.ruleTypeOptions.ranking")}</MenuItem>
								<MenuItem value="average">{t("grid.conditionalFormatting.ruleTypeOptions.average")}</MenuItem>
								<MenuItem value="unique">{t("grid.conditionalFormatting.ruleTypeOptions.unique")}</MenuItem>
							</Select>
						</FormControl>
					</SpacedGridItem>
					<SpacedGridItem maxCols={3}>
						<FormControl fullWidth>
							<InputLabel>{t("grid.conditionalFormatting.conditionLogic")}</InputLabel>
							<Select
								label={t("grid.conditionalFormatting.conditionLogic")}
								onChange={(e) => setNewRule({ ...newRule, andor: e.target.value as RuleAndOrType })}
								value={newRule.andor}
							>
								<MenuItem value="and">{t("grid.conditionalFormatting.conditionLogicOptions.and")}</MenuItem>
								<MenuItem value="or">{t("grid.conditionalFormatting.conditionLogicOptions.or")}</MenuItem>
							</Select>
						</FormControl>
					</SpacedGridItem>
					<SpacedGridItem maxCols={3}>
						<FormControlLabel
							control={
								<Checkbox
									checked={newRule.applyToRow || false}
									onChange={(e) => setNewRule({ ...newRule, applyToRow: e.target.checked })}
								/>
							}
							label={t("grid.conditionalFormatting.applyToRow")}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={newRule.defaultActive || false}
									onChange={(e) => setNewRule({ ...newRule, defaultActive: e.target.checked })}
								/>
							}
							label={t("grid.conditionalFormatting.defaultActive")}
						/>
					</SpacedGridItem>
				</SpacedGridContainer>
				<TabContext value={tabValue}>
					<Box sx={{ borderBottom: 1, borderColor: "divider", marginLeft: 1, marginRight: 1 }}>
						<TabList
							allowScrollButtonsMobile
							aria-label={t("grid.conditionalFormatting.conditionTabsAria")}
							onChange={tabChange}
							scrollButtons="auto"
							variant="scrollable"
						>
							{newRule.conditions.map((_condition, index) => (
								<Tab
									key={index + 1}
									label={t("grid.conditionalFormatting.conditionTab", { number: index + 1 })}
									value={`${index + 1}`}
								/>
							))}
							{newRule.conditions.length < 5 && (
								<Tab
									label={t("grid.conditionalFormatting.addCondition")}
									onClick={addCondition}
									value={`${newRule.conditions.length + 1}`}
								/>
							)}
						</TabList>
					</Box>

					{newRule.conditions.map((condition, index) => (
						<TabPanel key={index} sx={{ padding: 1, paddingTop: 2 }} value={`${index + 1}`}>
							<SpacedGridContainer>
								<SpacedGridItem maxCols={2}>
									<FormControl fullWidth>
										<InputLabel>{t("grid.conditionalFormatting.column")}</InputLabel>
										<Select
											label={t("grid.conditionalFormatting.column")}
											onChange={(e) => updateCondition(index, { column: e.target.value })}
											value={newRule.conditions[index].column}
										>
											{columns.map((col) => (
												<MenuItem key={col.field} value={col.field}>
													{camelToTitle(col.field || "")}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</SpacedGridItem>
								{showFor(["value"], newRule.ruleType) && (
									<SpacedGridItem maxCols={2}>
										<FormControl fullWidth>
											<InputLabel>{t("grid.conditionalFormatting.dataType")}</InputLabel>
											<Select
												label={t("grid.conditionalFormatting.dataType")}
												onChange={(e) => updateCondition(index, { dataType: e.target.value as "numeric" | "string" })}
												value={condition.dataType}
											>
												<MenuItem value="numeric">{t("grid.conditionalFormatting.dataTypes.numeric")}</MenuItem>
												<MenuItem value="string">{t("grid.conditionalFormatting.dataTypes.string")}</MenuItem>
											</Select>
										</FormControl>
									</SpacedGridItem>
								)}
								{showFor(["value"], newRule.ruleType) && (
									<SpacedGridItem key={index} maxCols={2}>
										<FormControl fullWidth>
											<InputLabel>{t("grid.conditionalFormatting.conditionType")}</InputLabel>
											{condition.dataType === "numeric" ? (
												<Select
													label={t("grid.conditionalFormatting.conditionType")}
													onChange={(e) => {
														updateCondition(index, {
															type: { [condition.dataType]: e.target.value as ConditionTypeNumeric }
														});
													}}
													value={condition.type?.numeric}
												>
													<MenuItem value="greaterThan">
														{t("grid.conditionalFormatting.conditionTypes.greaterThan")}
													</MenuItem>
													<MenuItem value="lessThan">
														{t("grid.conditionalFormatting.conditionTypes.lessThan")}
													</MenuItem>
													<MenuItem value="equalTo">{t("grid.conditionalFormatting.conditionTypes.equalTo")}</MenuItem>
													<MenuItem value="between">{t("grid.conditionalFormatting.conditionTypes.between")}</MenuItem>
												</Select>
											) : (
												<Select
													label={t("grid.conditionalFormatting.conditionType")}
													onChange={(e) => {
														updateCondition(index, {
															type: { [condition.dataType]: e.target.value as ConditionTypeString }
														});
													}}
													value={condition.type?.string}
												>
													<MenuItem value="contains">
														{t("grid.conditionalFormatting.conditionTypes.contains")}
													</MenuItem>
													<MenuItem value="notContains">
														{t("grid.conditionalFormatting.conditionTypes.notContains")}
													</MenuItem>
													<MenuItem value="startsWith">
														{t("grid.conditionalFormatting.conditionTypes.startsWith")}
													</MenuItem>
													<MenuItem value="endsWith">
														{t("grid.conditionalFormatting.conditionTypes.endsWith")}
													</MenuItem>
													<MenuItem value="equalTo">{t("grid.conditionalFormatting.conditionTypes.equalTo")}</MenuItem>
													<MenuItem value="notEqualTo">
														{t("grid.conditionalFormatting.conditionTypes.notEqualTo")}
													</MenuItem>
												</Select>
											)}
										</FormControl>
									</SpacedGridItem>
								)}

								{showFor(["average", "ranking", "unique"], newRule.ruleType) && (
									<SpacedGridItem key={index} maxCols={2}>
										<FormControl fullWidth>
											<InputLabel>{t("grid.conditionalFormatting.conditionType")}</InputLabel>
											{newRule.ruleType === "average" ? (
												<Select
													label={t("grid.conditionalFormatting.conditionType")}
													onChange={(e) => {
														updateCondition(index, { type: { ["average"]: e.target.value as ConditionTypeAverage } });
													}}
													value={condition.type?.average}
												>
													<MenuItem value="above">{t("grid.conditionalFormatting.conditionTypes.above")}</MenuItem>
													<MenuItem value="below">{t("grid.conditionalFormatting.conditionTypes.below")}</MenuItem>
												</Select>
											) : null}
											{newRule.ruleType === "ranking" ? (
												<Select
													label={t("grid.conditionalFormatting.conditionType")}
													onChange={(e) => {
														updateCondition(index, { type: { ["ranking"]: e.target.value as ConditionTypeRanking } });
													}}
													value={condition.type?.ranking}
												>
													<MenuItem value="top">{t("grid.conditionalFormatting.conditionTypes.top")}</MenuItem>
													<MenuItem value="bottom">{t("grid.conditionalFormatting.conditionTypes.bottom")}</MenuItem>
												</Select>
											) : null}
											{newRule.ruleType === "unique" ? (
												<Select
													label={t("grid.conditionalFormatting.conditionType")}
													onChange={(e) => {
														updateCondition(index, { type: { ["unique"]: e.target.value as ConditionTypeUnique } });
													}}
													value={condition.type?.unique}
												>
													<MenuItem value="unique">{t("grid.conditionalFormatting.conditionTypes.unique")}</MenuItem>
													<MenuItem value="duplicate">
														{t("grid.conditionalFormatting.conditionTypes.duplicate")}
													</MenuItem>
												</Select>
											) : null}
										</FormControl>
									</SpacedGridItem>
								)}

								{showFor(["value", "ranking"], newRule.ruleType) && (
									<SpacedGridItem maxCols={2}>
										{condition.dataType === "numeric" ? (
											<NumberFieldBase
												fullWidth
												label={
													condition.type?.["numeric"] === "between"
														? t("grid.conditionalFormatting.fromValue")
														: t("grid.conditionalFormatting.value")
												}
												onValueChange={(value) =>
													updateCondition(index, { value: value === null ? "" : String(value) })
												}
												value={condition.value ? Number(condition.value) : null}
											/>
										) : (
											<TextField
												fullWidth
												label={t("grid.conditionalFormatting.value")}
												onChange={(e) => updateCondition(index, { value: e.target.value })}
												value={condition.value}
											/>
										)}
									</SpacedGridItem>
								)}
								{showFor(["value"], newRule.ruleType, ["between"], condition.type?.[condition.dataType]) && (
									<SpacedGridItem maxCols={2}>
										<NumberFieldBase
											fullWidth
											label={t("grid.conditionalFormatting.toValue")}
											onValueChange={(value) => updateCondition(index, { value2: value === null ? "" : String(value) })}
											value={condition.value2 ? Number(condition.value2) : null}
										/>
									</SpacedGridItem>
								)}
								{index > 0 && (
									<SpacedGridItem maxCols={1} textAlign={"right"}>
										<RemoveButton
											color="inherit"
											onClick={() => {
												deleteCondition(index);
											}}
										>
											{t("grid.conditionalFormatting.removeCondition")}
										</RemoveButton>
									</SpacedGridItem>
								)}
							</SpacedGridContainer>
						</TabPanel>
					))}
				</TabContext>
				<Divider sx={{ marginTop: 2 }} />
				<Typography sx={{ display: "flex", justifyContent: "space-between", marginLeft: 1, marginTop: 2 }} variant="h6">
					{t("grid.conditionalFormatting.formatting")}{" "}
					<Select
						name="styleType"
						onChange={(e) => setNewRule({ ...newRule, styleType: e.target.value as RuleStyleType })}
						value={newRule.styleType}
						variant="standard"
					>
						<MenuItem value="single">{t("grid.conditionalFormatting.styleTypes.single")}</MenuItem>
						<MenuItem value="double">{t("grid.conditionalFormatting.styleTypes.double")}</MenuItem>
					</Select>
				</Typography>

				<SpacedGridContainer
					padding={1}
					sx={{
						alignItems: "stretch",
						justifyContent: "center"
					}}
				>
					<SpacedGridItem maxCols={3}>
						<MuiColorInput
							format="hex8"
							fullWidth
							label={t("grid.conditionalFormatting.backgroundColor")}
							onChange={(backgroundColor) => setNewRule({ ...newRule, style: { ...newRule.style, backgroundColor } })}
							value={newRule.style.backgroundColor}
						/>
					</SpacedGridItem>
					<SpacedGridItem maxCols={3}>
						<MuiColorInput
							format="hex8"
							fullWidth
							label={t("grid.conditionalFormatting.textColor")}
							onChange={(color) => setNewRule({ ...newRule, style: { ...newRule.style, color } })}
							value={newRule.style.color}
						/>
					</SpacedGridItem>
					<SpacedGridItem maxCols={3}>
						{newRule.styleType === "single" ? (
							<Box
								sx={{
									backgroundColor: newRule.style.backgroundColor,
									borderRadius: 1,
									color: newRule.style.color,
									padding: 2
								}}
							>
								{t("grid.conditionalFormatting.formatPreview")}
							</Box>
						) : (
							<Box
								sx={{
									backgroundImage: `linear-gradient(to right,${newRule.style.backgroundColor}, ${newRule.style2?.backgroundColor})`,
									borderRadius: 1,
									color: newRule.style?.color,
									padding: 2
								}}
							>
								{t("grid.conditionalFormatting.formatPreview")}
							</Box>
						)}
					</SpacedGridItem>
					{newRule.styleType === "double" && (
						<>
							<SpacedGridItem maxCols={3}>
								<MuiColorInput
									format="hex8"
									fullWidth
									label={t("grid.conditionalFormatting.backgroundColor")}
									onChange={(backgroundColor) =>
										setNewRule({ ...newRule, style2: { ...newRule.style2, backgroundColor } })
									}
									value={newRule.style2?.backgroundColor || ""}
								/>
							</SpacedGridItem>
							<SpacedGridItem maxCols={3}>
								<MuiColorInput
									format="hex8"
									fullWidth
									label={t("grid.conditionalFormatting.textColor")}
									onChange={(color) => setNewRule({ ...newRule, style2: { ...newRule.style2, color } })}
									value={newRule.style2?.color || ""}
								/>
							</SpacedGridItem>
							<SpacedGridItem maxCols={3}>
								{" "}
								<Box
									sx={{
										backgroundImage: `linear-gradient(to right,${newRule.style.backgroundColor}, ${newRule.style2?.backgroundColor})`,
										borderRadius: 1,
										color: newRule.style2?.color,
										padding: 2,
										textAlign: "right"
									}}
								>
									{t("grid.conditionalFormatting.formatPreview")}
								</Box>
							</SpacedGridItem>
						</>
					)}

					<SpacedGridItem maxCols={1} textAlign={"center"}>
						<SpacedGridContainer padding={1}>
							<SpacedGridItem maxCols={3}>
								{" "}
								<Button fullWidth onClick={addRule} variant="contained">
									{newRule.id ? t("grid.conditionalFormatting.updateRule") : t("grid.conditionalFormatting.addRule")}
								</Button>
							</SpacedGridItem>
							<SpacedGridItem maxCols={3}>
								<CancelButton
									onClick={() => {
										setNewRule(getNewRule());
										setAddEditRule(false);
										setTabValue("1");
									}}
									sx={{
										marginLeft: 2
									}}
								/>
							</SpacedGridItem>
							<SpacedGridItem maxCols={3}>
								{newRule.id !== "" && (
									<DeleteButton
										onClick={() => {
											deleteRule(newRule.id);
											setTabValue("1");
										}}
										sx={{
											marginLeft: 2
										}}
									/>
								)}
							</SpacedGridItem>
						</SpacedGridContainer>
					</SpacedGridItem>
				</SpacedGridContainer>
			</ModalDialog>
		);
	}

	return (
		<ModalDialog
			close={() => setFormattingPanelOpen(false)}
			open={formattingPanelOpen}
			title={t("grid.conditionalFormatting.title")}
		>
			<Typography sx={{ display: "flex", justifyContent: "space-between", margin: 1 }} variant="h6">
				{t("grid.conditionalFormatting.savedRules.title")}
				<AddButton
					onClick={() => {
						setAddEditRule(true);
					}}
					sx={{
						marginLeft: 2
					}}
				>
					{t("grid.conditionalFormatting.addRule")}
				</AddButton>
			</Typography>
			<SavedRules editRule={editRule} rules={rules} toggleActive={toggleActive} />
		</ModalDialog>
	);
};

export default FormattingPanel;
